import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Flag from 'react-flagpack';
import 'react-flagpack/dist/style.css';

import Screen from '../../components/common/Screen';
import NavBar from '../../components/common/NavBar';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import Button from '../../components/elements/Button';
import CheerCard from '../../components/common/CheerCard';
import { PurposePills } from '../../components/common/EventTags';
import { apiRequest, awardArt, teamPhotoOf } from '../../utils';
import BadgeSheet, { BadgeTile } from '../../components/common/BadgeSheet';
import './PublicProfile.css';

const userFallback = require('./../../assets/images/fallback/user.png');
/* A prize's picture is decided in ONE place for every screen that draws one (`awardArt` in utils):
   what was uploaded with the award, else our bundled art by name, else the generic cup. It used to
   be copied here and on the participants' award page, and neither copy knew about an uploaded
   picture — so a prize a customer invented showed its own art on neither screen. */

const yearOf = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return Number.isFinite(d.getFullYear()) ? d.getFullYear() : null;
};

// NavBar self-hides until the viewer is logged in (see NavBar.js), so it's
// safe to render here unconditionally.
//
// ★ The Back arrow is in the LAYOUT, so it is on all four states — loading, gated, not found and
//   the profile itself. A 404 with no way back is the worst version of the bug it fixes: this page
//   is where the community wall sends you, and until now the only way off it was the browser's own
//   gesture or the NavBar, which goes home and loses the wall you were reading.
// ★ `to` is only the COLD-OPEN fallback (see Back.js) and it is deliberately absent for a visitor
//   who is not logged in: a profile opened from a shared link should step back out of the app, not
//   into a member screen they cannot use.
const Layout = ({ children, authed }) => (
  <Screen>
    <Back to={authed ? '/member-home' : undefined} />
    <div className="top">{children}</div>
    <div className="bottom">
      <NavBar />
    </div>
  </Screen>
);

const Page = () => {
  const { t } = useTranslation(['common', 'awards']);
  const navigate = useNavigate();
  const params = useParams();

  // Prefer the URL-based id; fall back to legacy sessionStorage for old links.
  const userId = params.userId || sessionStorage.getItem('otherUserId');

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'gated' | 'notfound'
  const [openBadge, setOpenBadge] = useState(null);

  // Derive auth state from the response itself (single round-trip, no race).
  // null while loading → no NavBar yet; true/false once data arrives.
  const authed = data && typeof data.is_authenticated === 'boolean' ? data.is_authenticated : null;

  useEffect(() => {
    if (!userId) { setStatus('notfound'); return; }

    let cancelled = false;
    setStatus('loading');

    apiRequest('GET', `/user/${userId}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setStatus('ok');
      })
      .catch(() => {
        if (cancelled) return;
        // Sync cookie hint — may be stale, but it's the best we can do without
        // a dedicated auth round-trip. Errs on the "sign in" side for safety.
        const cookieAuthed = document.cookie.split(';').some((c) => c.trim().startsWith('is_authed=1'));
        setStatus(cookieAuthed ? 'notfound' : 'gated');
      });

    return () => { cancelled = true; };
  }, [userId]);

  const currentTeam = useMemo(() => {
    if (!data?.teams?.length) return null;
    const now = new Date();
    // First team whose event window is "now": event end >= today (or no end_date but date is today/future).
    const ongoing = data.teams.find((tm) => {
      const end = tm.end_date ? new Date(tm.end_date) : null;
      const start = tm.date ? new Date(tm.date) : null;
      if (end) return end >= now;
      if (start) return start >= now;
      return false;
    });
    return ongoing || null;
  }, [data]);

  const distinctCompanies = useMemo(() => {
    if (!data?.teams) return [];
    const seen = new Set();
    const out = [];
    for (const tm of data.teams) {
      const key = tm.company_id || tm.company_name;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({ id: tm.company_id, name: tm.company_name, photo_url: tm.company_photo_url });
    }
    return out;
  }, [data]);

  // ---------- LOADING ----------
  if (status === 'loading') {
    return (
      <Layout authed={authed}>
        <div className="pp-page">
          <div className="pp-skeleton pp-skel-hero" />
          <div className="pp-skeleton pp-skel-stats" />
          <div className="pp-skeleton pp-skel-row" />
          <div className="pp-skeleton pp-skel-row" />
        </div>
      </Layout>
    );
  }

  // ---------- GATED (private + not logged in) ----------
  if (status === 'gated') {
    return (
      <Layout authed={authed}>
        <div className="pp-page">
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="lock" /></div>
            <div className="pp-empty-title">{t('profileNotPublic')}</div>
            <p className="pp-empty-sub">{t('profileNotPublicDesc')}</p>
            <Button theme="primary" onClick={() => navigate('/login')}>{t('login')}</Button>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- NOT FOUND ----------
  if (status === 'notfound' || !data) {
    return (
      <Layout authed={authed}>
        <div className="pp-page">
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="info" /></div>
            <div className="pp-empty-title">{t('profileNotFound', { defaultValue: 'Profile not found' })}</div>
            <p className="pp-empty-sub">{t('profileNotFoundDesc', { defaultValue: 'This profile does not exist or is no longer available.' })}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- OK ----------
  const { full_name, photo_url, bio, country, instagram, facebook, linkedin, twitter,
          website, video_url, phone, stats, teams, awards, buddies } = data;
  const heroPhoto = photo_url || userFallback;

  /* WhatsApp was editable on /connections and returned by the API from the day the row was built,
     and nothing ever drew it — so the one channel people actually message each other on was the
     one missing here. wa.me wants digits and nothing else: no +, no spaces, no leading 00. */
  const waNumber = String(phone || '').replace(/\D/g, '').replace(/^00/, '');
  const hasLinks = !!(linkedin || instagram || waNumber || facebook || twitter || website
                      || video_url);

  /* What this person chose to say about themselves. Every one of these is optional and every one
     is drawn only when it carries something — a profile that has never met the editor renders
     exactly as it did before these fields existed. */
  const extras = data.profile || {};
  const chipRows = [['hobbies', 'pf_hobbies'], ['languages', 'pf_languages'],
                    ['ask_me_about', 'pf_ask_me_about']]
    .filter(([key]) => (extras[key] || []).length > 0);
  const factRows = [['based_in', 'pf_based_in'], ['superpower', 'pf_superpower'],
                    ['since_year', 'pf_since_year'], ['fun_fact', 'pf_fun_fact'],
                    ['motto', 'pf_motto']]
    .filter(([key]) => extras[key]);
  /* What they do here, drawn under the name rather than among the rows below: it is the one line
     of a profile you read before deciding whether to read the rest. The server has already
     resolved their own words against what the CRM imported, so there is one answer here. */
  const jobLine = [extras.role, extras.department].filter(Boolean).join(' · ');
  const picks = Object.entries(extras.this_or_that || {});
  const openTo = extras.open_to || [];
  const hasExtras = chipRows.length > 0 || factRows.length > 0
    || picks.length > 0 || openTo.length > 0;

  return (
    <Layout authed={authed}>
      <div className="pp-page">

        {/* HERO */}
        <section className="pp-hero">
          <div className="pp-hero-bg" style={{ backgroundImage: `url(${heroPhoto})` }} />
          <div className="pp-hero-inner">
            <div className="pp-avatar-ring">
              <img src={heroPhoto} alt="" className="pp-avatar" />
            </div>

            <h1 className="pp-name">
              {full_name}
              {country && (
                <span className="pp-flag-wrap">
                  <Flag code={country} size="m" hasBorderRadius hasBorder={false} hasDropShadow={false} />
                </span>
              )}
            </h1>

            {jobLine && <p className="pp-job">{jobLine}</p>}

            {bio && <p className="pp-bio">{bio}</p>}

            {/* ★ Ordered the way people actually reach each other — LinkedIn, Instagram, WhatsApp,
                Facebook — and only then the rest. This row is the whole point of the page for a
                colleague who came here to get in touch. */}
            {hasLinks && (
              <div className="pp-socials">
                {linkedin && (
                  <Link className="pp-social" target="_blank" rel="noopener noreferrer"
                        to={linkedin.startsWith('http') ? linkedin : `https://linkedin.com/in/${linkedin}`}>
                    <Icon name="linkedin" pack="brands" />
                  </Link>
                )}
                {instagram && (
                  <Link className="pp-social" target="_blank" rel="noopener noreferrer"
                        to={instagram.startsWith('http') ? instagram : `https://instagram.com/${instagram}`}>
                    <Icon name="instagram" pack="brands" />
                  </Link>
                )}
                {waNumber && (
                  <Link className="pp-social" target="_blank" rel="noopener noreferrer"
                        to={`https://wa.me/${waNumber}`}>
                    <Icon name="whatsapp" pack="brands" />
                  </Link>
                )}
                {facebook && (
                  <Link className="pp-social" target="_blank" rel="noopener noreferrer"
                        to={facebook.startsWith('http') ? facebook : `https://facebook.com/${facebook}`}>
                    <Icon name="facebook" pack="brands" />
                  </Link>
                )}
                {twitter && (
                  <Link className="pp-social" target="_blank" rel="noopener noreferrer"
                        to={twitter.startsWith('http') ? twitter : `https://twitter.com/${twitter}`}>
                    <Icon name="twitter" pack="brands" />
                  </Link>
                )}
                {/* Two links the person could always fill in and that nothing ever drew: their own
                    site and the video on their profile. Not brand glyphs — there is no brand. */}
                {website && (
                  <Link className="pp-social" target="_blank" rel="noopener noreferrer"
                        to={website.startsWith('http') ? website : `https://${website}`}>
                    <Icon name="globe" />
                  </Link>
                )}
                {video_url && (
                  <Link className="pp-social" target="_blank" rel="noopener noreferrer"
                        to={video_url.startsWith('http') ? video_url : `https://${video_url}`}>
                    <Icon name="photo-video" />
                  </Link>
                )}
              </div>
            )}

            {/* ★ Only the person themselves ever sees this. An empty row renders as nothing at
                all, which is right for a visitor and is exactly why nobody knew the fields
                existed — the one reader who can do something about it gets told where. */}
            {!hasLinks && data.is_self && (
              <Link className="pp-addlinks" to="/connections">
                <Icon name="share-square" className="me-2" />
                {t('ppAddLinks')}
              </Link>
            )}
          </div>
        </section>

        {/* RECOGNISED FOR — what colleagues said this person did (F7). Directly under the hero,
            above everything they said about themselves, because it is the one block on this page
            written by somebody else: what other people noticed outranks what you told us, and it
            is only here at all because the person it is about chose to show each one.

            The chips are the summary — three cheers all naming one value is a fact a list of
            three paragraphs does not state — and the sentences behind them are what makes it
            true rather than decorative. Absent entirely when there are none. */}
        {(data.recognised_for || []).length > 0 && (
          <section className="pp-section pp-recog">
            <h2 className="pp-section-title">{t('ppRecognisedFor')}</h2>
            <div className="pp-recog__chips">
              {data.recognised_for.map(({ chip, n }) => (
                <span className="pp-recog__row" key={`${chip.kind}-${chip.id}`}>
                  <PurposePills items={[chip]} />
                  {n > 1 && <span className="pp-recog__n">×{n}</span>}
                </span>
              ))}
            </div>
            {(data.cheers || []).map((cheer) => (
              <CheerCard key={cheer.id} cheer={cheer} />
            ))}
          </section>
        )}

        {/* ABOUT — what they said about themselves. Between the hero and the tallies on purpose:
            a person is who they are before they are three numbers. */}
        {hasExtras && (
          <section className="pp-section pp-about">
            {chipRows.map(([key, label]) => (
              <div className="pp-about__row" key={key}>
                <div className="pp-about__label">{t(label)}</div>
                <div className="pp-about__chips">
                  {extras[key].map((word) => (
                    <span className="pp-about__chip" key={word}>{word}</span>
                  ))}
                </div>
              </div>
            ))}

            {openTo.length > 0 && (
              <div className="pp-about__row">
                <div className="pp-about__label">{t('pf_open_to')}</div>
                <div className="pp-about__chips">
                  {openTo.map((token) => (
                    <span className="pp-about__chip pp-about__chip--open" key={token}>
                      {/* Translated for the five the platform suggests, as typed for anything
                          somebody wrote themselves — see CommunityWall's chip row. */}
                      {t(`pfOpen_${token}`, { defaultValue: token })}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {factRows.map(([key, label]) => (
              <div className="pp-about__row" key={key}>
                <div className="pp-about__label">{t(label)}</div>
                <div className="pp-about__value">{extras[key]}</div>
              </div>
            ))}

            {picks.length > 0 && (
              <div className="pp-about__row">
                <div className="pp-about__label">{t('pf_this_or_that')}</div>
                <div className="pp-about__chips">
                  {picks.map(([key, value]) => (
                    <span className="pp-about__chip" key={key}>
                      {t(`pfTot_${key}`)}: {t(`pfTotv_${value}`)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* STATS */}
        {stats && (
          <div className="pp-stats">
            <div className="pp-stat">
              <div className="pp-stat-value">{stats.team_count || 0}</div>
              <div className="pp-stat-label">{t('teams')}</div>
            </div>
            <div className="pp-stat">
              <div className="pp-stat-value">{stats.battle_count || 0}</div>
              <div className="pp-stat-label">{t('challenges')}</div>
            </div>
            <div className="pp-stat">
              <div className="pp-stat-value">{stats.award_count || 0}</div>
              <div className="pp-stat-label">{t('awards')}</div>
            </div>
          </div>
        )}

        {/* CURRENT TEAM */}
        {currentTeam && (
          <section className="pp-section">
            <Link to={`/public-team/${currentTeam.id}`} className="pp-current">
              <img src={teamPhotoOf(currentTeam, currentTeam.photo_url, currentTeam.company_photo_url)} alt="" className="pp-current-img" />
              <div className="pp-current-meta">
                <p className="pp-current-eyebrow">{t('currentlyCompeting', { defaultValue: 'Currently competing' })}</p>
                <p className="pp-current-team">{currentTeam.name}</p>
                <p className="pp-current-event">
                  {currentTeam.event_name}{currentTeam.company_name ? ` · ${currentTeam.company_name}` : ''}
                </p>
              </div>
              <Icon name="angle-right" />
            </Link>
          </section>
        )}

        {/* TEAMS */}
        {teams?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('teams')}</h2>
              <span className="pp-section-count">{teams.length}</span>
            </header>
            <div className="pp-team-grid">
              {teams.map((team) => (
                <Link
                  key={team.id}
                  to={`/public-team/${team.id}`}
                  className="pp-team-card"
                >
                  <img src={teamPhotoOf(team, team.photo_url, team.company_photo_url)} alt="" className="pp-team-img" />
                  <div className="pp-team-fade" />
                  {yearOf(team.date) && <span className="pp-team-year">{yearOf(team.date)}</span>}
                  <div className="pp-team-meta">
                    <div className="pp-team-name">{team.name}</div>
                    {team.company_name && <div className="pp-team-company">{team.company_name}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* AWARDS */}
        {awards?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('awards')}</h2>
              <span className="pp-section-count">{awards.length}</span>
            </header>
            <div className="pp-awards-grid">
              {awards.map((award) => {
                const inner = (
                  <>
                    <div className="pp-award-img-wrap">
                      <img src={awardArt(award.award_name, award.image)} alt="" className="pp-award-img" />
                    </div>
                    <div className="pp-award-name">
                      {t(award.award_name, { ns: 'awards', defaultValue: award.award_name.replace(/(?!^)([A-Z])/g, ' $1') })}
                    </div>
                    {yearOf(award.event_date) && <div className="pp-award-event">{yearOf(award.event_date)}</div>}
                  </>
                );
                /* ★ A prize with this person's OWN name on it has no team behind it, and a Link to
                   /public-team/undefined is a tile that looks the same and goes nowhere. It is
                   drawn as a plain tile instead — which is also the more honest picture: this one
                   was not won by a team. */
                return award.team_id ? (
                  <Link key={award.id} to={`/public-team/${award.team_id}`} className="pp-award">
                    {inner}
                  </Link>
                ) : (
                  <div key={award.id} className="pp-award">{inner}</div>
                );
              })}
            </div>
          </section>
        )}

        {/* BADGES — earned for life, granted at night; colleagues see them too (never the score
            behind a points badge, which the API does not send). */}
        {data.badges?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('badges')}</h2>
              <span className="pp-section-count">{data.badges.length}</span>
            </header>
            <div className="pp-awards-grid">
              {data.badges.map((b) => (
                <BadgeTile key={b.id} badge={b} onOpen={setOpenBadge}>
                  {yearOf(b.awarded_on) && <div className="pp-award-event">{yearOf(b.awarded_on)}</div>}
                </BadgeTile>
              ))}
            </div>
          </section>
        )}

        {/* COMPANIES */}
        {distinctCompanies.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('companies', { defaultValue: 'Companies' })}</h2>
              <span className="pp-section-count">{distinctCompanies.length}</span>
            </header>
            <div className="pp-company-row">
              {distinctCompanies.map((c) => {
                const inner = (
                  <>
                    <div className="pp-company-logo-wrap">
                      {c.photo_url
                        ? <img src={c.photo_url} alt="" className="pp-company-logo" />
                        : <span className="pp-company-initial">{(c.name || '?').charAt(0).toUpperCase()}</span>}
                    </div>
                    <span className="pp-company-name">{c.name}</span>
                  </>
                );
                if (c.id) {
                  return (
                    <Link
                      key={c.id}
                      to={`/public-company/${c.id}`}
                      className="pp-company-chip"
                    >
                      {inner}
                    </Link>
                  );
                }
                return (
                  <span key={c.name} className="pp-company-chip pp-company-chip-static">{inner}</span>
                );
              })}
            </div>
          </section>
        )}

        {/* BEST BUDDIES */}
        {buddies?.length > 0 && (
          <section className="pp-section">
            <header className="pp-section-header">
              <h2 className="pp-section-title">{t('bestBuddies', { defaultValue: 'Best buddies' })}</h2>
              <span className="pp-section-count">{buddies.length}</span>
            </header>
            <div className="pp-buddy-grid">
              {buddies.map((b) => {
                const reachable = b.is_public || authed;
                const Wrapper = reachable ? Link : 'div';
                const wrapperProps = reachable ? { to: `/u/${b.id}` } : {};
                return (
                  <Wrapper
                    key={b.id}
                    className={`pp-buddy ${reachable ? '' : 'pp-buddy-locked'}`}
                    {...wrapperProps}
                  >
                    <img src={b.photo_url || userFallback} alt="" className="pp-buddy-img" />
                    <div className="pp-buddy-name">{b.full_name}</div>
                    <div className="pp-buddy-shared">
                      {b.shared_teams} {b.shared_teams === 1 ? t('team') : t('teams')}
                    </div>
                  </Wrapper>
                );
              })}
            </div>
          </section>
        )}

      </div>
      <BadgeSheet badge={openBadge} onClose={() => setOpenBadge(null)} />
    </Layout>
  );
};

export default Page;
